/**
 * @ngdoc service
 * @name previewer
 * @description
 * This service provides with the methods to create previews for Media Objects.
 *
 * @memberof common
 */
class Previewer {
  /**
   * Class constructor.
   * @param {Service} $http          The service to make all the http requests.
   * @param {Service} $q             Most of the file methods make use of a callback,
   *                                 so we use `$q` to wrap all the async functionalities.
   * @param {Object}  appConstant    The app configuration dictionary.
   * @param {Service} fileUtils      To be handle local files.
   * @param {Object}  MEDIA_STATUSES Used to set the status of the media on the queue.
   * @ignore
   */
  constructor(
    $http,
    $q,
    appConstant,
    fileUtils,
    MEDIA_STATUSES,
  ) {
    'ngInject';

    /**
     * The local reference to the `$http` service.
     * @type {Object}
     */
    this.$http = $http;
    /**
     * The local reference to the `$q` service.
     * @type {Object}
     */
    this.$q = $q;
    /**
     * The Previewer service base URL.
     * @type {String}
     */
    this.previewerServiceURL = appConstant.imagePreviewerJS.url;
    /**
     * Place holder URL image for non previewabled Files.
     * @type {String}
     */
    this.defaultImageURL = appConstant.noPreviewImage.url;
    /**
     * The local reference to the `fileUtils` service.
     * @type {Service}
     */
    this.fileUtils = fileUtils;
    /**
     * The local reference to the media statuses object.
     * @type {Object}
     */
    this.MEDIA_STATUSES = MEDIA_STATUSES;
  }
  /**
   * Parse the previewer response into a new set o Media's properties
   * to send as reponse from the preview process.
   *
   * @param  {Object} response  A previewer's response Object.
   * @param  {String} newStatus A MEDIA_STATUSES value.
   * @return {Object}           A new set of Media's property.
   */
  _getMediaProperties(response, newStatus) {
    return {
      status: newStatus,
      images: {
        normal: (response.base64 || response.data.url),
        thumbnail: (response.base64 || response.data.thumbnail),
      },
    };
  }
  /**
   * Use the fileUtils services to determinate if a File Object
   * is an valid Image for the PreviewerJS service.
   *
   * @param  {Object} file file object.
   * @return {Object}
   * @property {Boolean} isPreviewable the file can be uploaded to the
   *                                   preview service.
   * @property {String}  status        the Media's status that should be used.
   */
  _validateMediaFile(file) {
    const isValidImage = this.fileUtils.isValidImage(file);
    const rightSize = this.fileUtils.hasRightSize(file);
    const result = {
      isPreviewable: (isValidImage && rightSize),
      status: this.MEDIA_STATUSES.preview,
    };

    if (!isValidImage) {
      result.status = this.MEDIA_STATUSES.invalidImage;
    } else if (!rightSize) {
      result.status = this.MEDIA_STATUSES.invalidSize;
    }
    return result;
  }
  /**
   * Makes a request using `$http` to the previewerServiceURL.
   * @param {Object} file The HTML file object.
   * @param {Object} config The configuration for the `$http` call.
   * @return {Promise}
   */
  _upload(file, config = {}) {
    /**
     * Create the FormData Object and add the file element.
     */
    const formData = new FormData();
    formData.append('file', file);

    /**
     * Finally, return the request result.
     */
    return this.$http(Object.assign({
      url: this.previewerServiceURL,
      data: formData,
      method: 'POST',
      transformRequest: angular.identity,
      headers: {
        'Content-Type': undefined,
      },
    }, config));
  }
  /**
   * This method handle the creation of previews of a given Media list.
   * It will upload the asset to our
   * [ImagePreviewerJS](https://github.com/Olapic/ImagePreviewerJS) or retrieve
   * the Base64 representation of the image.
   * It will also propertly update the status of the Media during the process.
   *
   * @param {Array} workingMedia The list of Media Objects to be processed.
   * @return {Array}             List of Promises, one by each Media;
   *                             Each Promise will resolve with a new set of
   *                             Media's properties.
   */
  createPreview(workingMedia) {
    return workingMedia.map((media) => {
      const validateMediaFile = this._validateMediaFile(media.file);
      let promise = {};
      /**
       * Determinate which will be the preview method; upload the asset
       * or use Base64.
       */

      if (validateMediaFile.isPreviewable) {
        promise = this._upload(media.file)
        .then((response) => this._getMediaProperties(
          response,
          this.MEDIA_STATUSES.previewOk,
        ));
      } else if (validateMediaFile.status === this.MEDIA_STATUSES.invalidImage) {
        /**
         * Reject the promise with the MEDIA_STATUSES which
         * identify an Invalid Image.
         */
        promise = this.$q.reject(this.MEDIA_STATUSES.invalidImage);
      } else {
        /**
         * If the image is too big to generate a preivew;
         * we're going to return the Base64.
         */
        promise = this.fileUtils.readFile(media.file)
        .then((response) => this._getMediaProperties(
          response,
          validateMediaFile.status,
        ));
      }

      /**
       * In any case, we have to parse the response from
       * the previewer if it fail to include the default
       * ImageURL
       */
      return promise.catch((status) => {
        const rejectStatus = this.MEDIA_STATUSES[status]
          ? status
          : this.MEDIA_STATUSES.previewFail;

        return this._getMediaProperties({
          base64: this.defaultImageURL,
        }, rejectStatus);
      });
    });
  }
}

export default Previewer;



// WEBPACK FOOTER //
// ./src/frontend/modules/common/services/previewer.js